import { useState } from "react";
import type { GscSample, Ga4Sample } from "../lib/api";

/**
 * Dataset selection by content.
 *
 * Dataset names carry no information about which site they cover, so each
 * candidate is shown with real URLs pulled from it. Recognition beats naming.
 */
export function DatasetPicker({
  gsc, ga4, onConfirm, busy,
}: {
  gsc: GscSample[];
  ga4: Ga4Sample[];
  onConfirm: (sel: { gsc: GscSample | null; ga4: Ga4Sample | null }) => void;
  busy?: boolean;
}) {
  const [gscPick, setGscPick] = useState<string | null>(
    gsc.length === 1 ? key(gsc[0]) : null
  );
  const [ga4Pick, setGa4Pick] = useState<string | null>(
    ga4.length === 1 ? key(ga4[0]) : null
  );

  return (
    <>
      <section className="panel">
        <h2>Which Search Console export?</h2>
        {gsc.length === 0 ? (
          <p className="muted">
            None found. The app will fall back to the Search Console API.
          </p>
        ) : (
          <>
            <p className="muted">
              {gsc.length === 1
                ? "One export found. Check the pages below match your site."
                : `${gsc.length} exports found. Each covers one property.`}
            </p>
            {gsc.map((s) => (
              <Card
                key={key(s)}
                selected={gscPick === key(s)}
                onSelect={() => setGscPick(key(s))}
                title={domainOf(s.siteUrl) ?? s.dataset}
                subtitle={`${s.project} · ${s.dataset}`}
                error={s.error}
              />
            ))}
          </>
        )}
      </section>

      <section className="panel">
        <h2>Which GA4 export?</h2>
        {ga4.length === 0 ? (
          <p className="muted">
            None found. If you enabled the export today, its first daily table
            appears tomorrow. Until then the funnel runs on the Data API at
            landing-page level only.
          </p>
        ) : (
          <>
            <p className="muted">
              {ga4.length === 1
                ? "One export found. Check the pages below match your site."
                : `${ga4.length} exports found.`}
            </p>
            {ga4.map((s) => (
              <Card
                key={key(s)}
                selected={ga4Pick === key(s)}
                onSelect={() => setGa4Pick(key(s))}
                // GA4 has no site_url, so the hostname its own landing pages
                // resolve to is the only reliable identifier.
                title={s.topHosts[0] ?? `Property ${s.propertyId}`}
                subtitle={`${s.project} · ${s.dataset}`}
                error={s.error}
              />
            ))}
          </>
        )}
      </section>

      <button
        disabled={busy || (gsc.length > 0 && !gscPick)}
        onClick={() => onConfirm({
          gsc: gsc.find((s) => key(s) === gscPick) ?? null,
          ga4: ga4.find((s) => key(s) === ga4Pick) ?? null,
        })}
      >
        {busy ? "Saving" : "Use these sources"}
      </button>
      {gsc.length > 0 && !gscPick && (
        <p className="caveat" style={{ marginTop: ".5rem" }}>
          Select a Search Console export above to continue.
        </p>
      )}
    </>
  );
}

function Card({ selected, onSelect, title, subtitle, error }: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
  error?: string;
}) {
  return (
    <div
      className={`ds-card ${selected ? "selected" : ""}`}
      onClick={onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
    >
      <div className="ds-head">
        <span className={`ds-radio ${selected ? "on" : ""}`} aria-hidden="true" />
        <div>
          <div className="ds-title">{title}</div>
          <div className="ds-sub num">{subtitle}</div>
        </div>
      </div>
      {error && <p className="caveat">{error}</p>}
    </div>
  );
}

const key = (s: { project: string; dataset: string }) => `${s.project}/${s.dataset}`;

/** sc-domain:example.com and https://www.example.com/ both read as the domain. */
function domainOf(siteUrl: string | null): string | null {
  if (!siteUrl) return null;
  return siteUrl
    .replace(/^sc-domain:/, "")
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
}
